/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useState
} from 'react';
import {
    providerConfigRegistry,
    updateProviderParametersState
} from '../Common/providerConfigRegistry';
import Anatomy from '../Common/Anatomy';
import InputValidation from 'shared/components/InputValidation';
import labHelper from 'builders/builderInternal/classes/AILabHelper';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    Label
} from 'shared/components/ui/label';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Separator
} from 'shared/components/ui/separator';
import {
    Input
} from 'shared/components/ui/input';
import {
    Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Play
} from 'lucide-react';
import {
    Output
} from './Output';
import {
    unifiedModelWSService
} from 'shared/api/services/PROJECT-O/WEBSOCKETS/UnifiedModel.websocket.service';
import {
    Context
} from 'shared/context/GlobalState';
import CommonModelSelectionModal from '../Common/CommonModelSelectionModal';
import {
    apiKeyService
} from 'shared/api';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    normalizePredictionOutput
} from '../Common/predictionUtils';

export default function InstructionForm({
    form, setForm, handleReloadModels, modelProviders, update = false
}) {

    const [output, setOutput] = useState('');
    const [btnLoading, setBtnLoading] = useState(false);
    const [inputData, setInputData] = useState('');
    const {
        selectedOrganization
    } = useContext(OrganizationContext);
    const [apiKeys, setApiKeys] = useState([]);

    const {
        addNewNotifcation, user
    } = useContext(Context);

    const handleParameter = (data) => updateProviderParametersState(form.model_type, data, setForm);

    const renderProviderConfiguration = () => {
        if (!form.model_type) return null;
        const configEntry = providerConfigRegistry[form.model_type];
        if (!configEntry) return null;
        const ConfigComponent = configEntry.component;
        const props = configEntry.getProps({
            form,
            handleParameter
        });
        return <ConfigComponent {...props} />;
    };

    const selectedModel = (data) => {
        setForm(prev => ({
            ...prev,
            selectedModel: data.modelName,
            model_type: data.modelType
        }));

        // Store the full model data for OnPrem models to access api_key and base_url
        if (data.modelType === 'OnPrem') {
            const fullModelData =

                modelProviders.find(model =>
                    model.model_id === data.modelName &&
                    model.provider_type === data.modelType
                );

            if (fullModelData) {
                setForm(prev => ({
                    ...prev,
                    fullModelData: fullModelData
                }));
            }
        } else {
            setForm(prev => ({
                ...prev,
                fullModelData: null
            }));
        }
    };

    const handleSendPrompt = async (e) => {
        e.preventDefault();

        if (!form.selectedModel) {
            return addNewNotifcation('Please select a foundation model.', 'warning');
        } else if (!form.instruction.trim()) {
            return addNewNotifcation('Please enter an instruction.', 'warning');
        }

        try {
            setBtnLoading(true);
            setOutput('');

            let params = form?.watsonParameters;
            if (form.model_type === 'IBM') {
                if (form.watsonParameters.mode === 'greedy') {
                    params = Object.fromEntries(
                        Object.entries(form.watsonParameters).filter(([key]) => key === 'max_new_tokens' || key === 'mode' || key === 'stop_sequences')
                    );
                }
            }

            if (update === true) {
                const body = {
                    org_id: form.org_id,
                    sub_org_id: form.sub_org_id,
                    unique_name: form?.unique_name,
                    query: inputData.trim(),
                    image_urls: [],
                };

                // Add api_key and base_url for OnPrem models and validate
                if (form?.model_type === 'OnPrem') {
                    const modelInfo = form?.fullModelData || {};
                    const apiKey = modelInfo.api_key || modelInfo.apiKey;
                    const baseUrl = modelInfo.base_url || modelInfo.baseUrl || modelInfo.url;
                    if (!apiKey || !baseUrl) {
                        addNewNotifcation('Missing OnPrem credentials. Please ensure api_key and base_url are available for the selected model.', 'warning');
                        setBtnLoading(false);
                        return;
                    }
                    body.api_key = apiKey;
                    body.base_url = baseUrl;
                }

                const res = await unifiedModelWSService.predict(body, form?.model_type);
                setOutput(normalizePredictionOutput(res.result.prediction));
            } else {
                const body = {
                    modelId: form.selectedModel,
                    prompt: labHelper.ConstructPromptUnified({
                        instruction: form.instruction.trim(),
                        context: form.context.trim(),
                        target_format: form.targetFormat.trim(),
                        sample_input: form.sampleInput.trim(),
                        sample_output: form.sampleOutput.trim(),
                    }),
                    parameters: form.model_type === 'IBM' ? params :
                        form.model_type === 'Groq' ? form.groqParameters :
                            form.model_type === 'SambaNova' ? form.sambaNovaParameters :
                                form.model_type === 'OnPrem' ? form.onPremParameters :
                                    form.model_type === 'Cerebras' ? form.cerebrasParameters :
                                        form.model_type === 'Gemini' ? form.geminiParameters :
                                            form.model_type === 'Mistral' ? form.mistralParameters :
                                                form.model_type === 'Novita' ? form.novitaParameters :
                                                    form.groqParameters,
                    query: inputData.trim()
                };

                // Add api_key and base_url for OnPrem models and validate
                if (form?.model_type === 'OnPrem') {
                    const modelInfo = form?.fullModelData || {};
                    const apiKey = modelInfo.api_key || modelInfo.apiKey;
                    const baseUrl = modelInfo.base_url || modelInfo.baseUrl || modelInfo.url;
                    if (!apiKey || !baseUrl) {
                        addNewNotifcation('Missing OnPrem credentials. Please ensure api_key and base_url are available for the selected model.', 'warning');
                        setBtnLoading(false);
                        return;
                    }
                    body.api_key = apiKey;
                    body.base_url = baseUrl;
                }

                const res = await unifiedModelWSService.experiment(body, form.model_type, selectedOrganization._id, form.sub_org_id || '');
                setOutput(normalizePredictionOutput(res.result.prediction));
            }

        } catch (error) {
            if (typeof error === 'string' && error.includes('rate_limit_exceeded')) {
                addNewNotifcation('Model rate limit exceeded.', 'warning');
            } else if (typeof error === 'object' && error?.websocket_error) {
                addNewNotifcation('Error generating response.', 'danger');
            } else {
                addNewNotifcation('Error generating response.', 'danger');
            }

        } finally {
            setBtnLoading(false);
        }
    };

    useEffect(() => {
        // check for organization api keys
        if (selectedOrganization.byoKeys && selectedOrganization.byoKeys.length > 0) {
            const ibmKeys = selectedOrganization.byoKeys.find(item => item.apiType === 'IBM');
            if (ibmKeys) {
                setApiKeys(prev => ([...prev, {
                    ...ibmKeys,
                    name: ibmKeys.name,
                    value: ibmKeys._id
                }]));
            }
        }
        // check for user api keys
        const fetchApiKeys = async () => {
            try {
                const res = await apiKeyService.listApiKeys('user', user._id);
                const ibmKeys = res.data.find(item => item.apiType === 'IBM');
                if (ibmKeys) {
                    setApiKeys(prev => ([...prev, {
                        ...ibmKeys,
                        name: ibmKeys.name,
                        value: ibmKeys._id
                    }]));
                }
            } catch (error) { /* empty */

            }
        };

        fetchApiKeys();

    }, []);

    useEffect(() => {
        if (update) return;
        if (apiKeys.length > 0) {
            const ibmKeys = apiKeys.find(item => item.levelType === 'organization');
            if (ibmKeys) {
                setForm(prev => ({
                    ...prev,
                    level_type: ibmKeys.levelType,
                    api_key_id: ibmKeys._id
                }));
            } else {
                const userKey = apiKeys.find(item => item.levelType === 'user');
                if (userKey) {
                    setForm(prev => ({
                        ...prev,
                        level_type: userKey.levelType,
                        api_key_id: userKey._id
                    }));
                }
            }
        }

    }, [apiKeys]);

    const handleSelectApiKey = (value) => {
        const ibmKeys = apiKeys.find(item => item.value === value);
        if (ibmKeys) {
            setForm(prev => ({
                ...prev,
                level_type: ibmKeys.levelType,
                api_key_id: ibmKeys._id
            }));
        }
    };

    return (
        <div>
            <div className='flex gap-2'>
                <div className="w-full">
                    <CommonModelSelectionModal
                        handleReloadModels={handleReloadModels}
                        modelProviders={modelProviders}
                        finalModel={{
                            modelName: form.selectedModel,
                            modelType: form.model_type
                        }}
                        onModelSelect={selectedModel}
                    />
                </div>
                {renderProviderConfiguration()}
            </div>

            {form.model_type === 'IBM' && form.selectedModel && (
                <div className='mt-3'>
                    <Label className='text-right mt-2'> API connection </Label>
                    <Select value={form.api_key_id || 'default'} onValueChange={handleSelectApiKey} id="apikey" className="w-full">
                        <SelectTrigger>
                            <SelectValue placeholder="Select apikey" />
                        </SelectTrigger>
                        <SelectContent>
                            {apiKeys.length > 0 ? (
                                apiKeys.map((item) => (
                                    <SelectItem key={item.name} value={item.value}>
                                        {item.name} <small className='text-muted-foreground text-sm'> {`(${item.levelType} key)`} </small>
                                    </SelectItem>
                                ))
                            ) : (
                                <SelectItem value="default">Default API Key</SelectItem>
                            )}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <Separator className='my-3' />

            <ScrollArea className="max-h-[360px]">
                <div className="space-y-2 px-1 pt-4">
                    <div>
                        <Label htmlFor="name" className="text-right">
                            Unique Name
                        </Label>
                        <InputValidation
                            id="name"
                            value={form.unique_name}
                            disabled={update}
                            onChange={(e) => setForm(prev => ({
                                ...prev,
                                unique_name: e.target.value
                            }))}
                            className="col-span-3"
                            placeholder="Enter a unique name for your instruction"
                            validation={labHelper.validateUniqueName}
                        />
                    </div>
                    <div>
                        <Label htmlFor="description" className="text-right">
                            Description
                        </Label>
                        <Textarea
                            id="description"
                            value={form.modelDescription}
                            onChange={(e) => setForm(prev => ({
                                ...prev,
                                modelDescription: e.target.value
                            }))}
                            className="col-span-3"
                            placeholder="Enter description for your instruction"
                        />
                    </div>
                </div>
                <Anatomy instructionForm={form} setInstructionForm={setForm} isExecute={false} />

                <Separator />

                <div className='mt-3 px-2'>
                    <div className='space-y-2'>
                        <Label> Input data </Label>
                        <form onSubmit={handleSendPrompt} className='flex flex-row gap-2'>
                            <Input value={inputData} onChange={(e) => setInputData(e.target.value)} placeholder='input data' />
                            <Button type='submit' disabled={btnLoading} onSubmit={handleSendPrompt} className='px-1' size='icon'>
                                <Play size={16} className='' />
                            </Button>
                        </form>
                    </div>

                    <div className='space-y-2 mt-3'>
                        <Output output={output} />
                    </div>
                </div>
            </ScrollArea>
        </div>
    );
}